/**
 * Loads one saved run, plus the suite catalog for model labels and the judge prompt
 * versions. Reviews, judge flags and suggestion decisions are written through
 * the API and patched into the loaded run, not refetched.
 */
import { useCallback, useEffect, useState } from 'react';
import { evalsService } from '../services/evalsService';
import { isActiveRun, type EvalReview, type EvalRunDetail, type ListEvalSuitesResponse } from '../types/evalTypes';

const POLL_MS = 5_000;

export const useEvalRunDetail = (runId: string) => {
  const [detail, setDetail] = useState<EvalRunDetail | null>(null);
  const [catalog, setCatalog] = useState<ListEvalSuitesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [run, suites] = await Promise.all([evalsService.getRun(runId), evalsService.listSuites()]);
      setDetail(run);
      setCatalog(suites);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to load the eval run.');
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => {
    void load();
  }, [load]);

  // While the run, or one of its re-grades, is active, read it again every few seconds,
  // without the loading state. A failed read is left for the next one; the run goes on.
  const active = detail !== null && [detail.run, ...detail.regrades].some((run) => isActiveRun(run.status));
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => {
      evalsService.getRun(runId).then(setDetail, () => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [active, runId]);

  /** Throws on failure. */
  const cancelRun = useCallback(async () => {
    const run = await evalsService.cancelRun(runId);
    setDetail((previous) => (previous ? { ...previous, run: { ...previous.run, status: run.status, cancelRequested: run.cancelRequested } } : previous));
  }, [runId]);

  /** Throws on failure, so the form can show the error. */
  const saveReview = useCallback(
    async (decision: EvalReview['decision'], note: string) => {
      const review = await evalsService.setReview(runId, decision, note);
      setDetail((previous) => (previous ? { ...previous, run: { ...previous.run, review } } : previous));
    },
    [runId],
  );

  /** An empty `checkIds` clears the flag. Throws on failure. */
  const saveJudgeFlag = useCallback(
    async (resultId: string, checkIds: string[], note: string) => {
      const judgeFlag = await evalsService.setJudgeFlag(runId, resultId, checkIds, note);
      setDetail((previous) =>
        previous
          ? { ...previous, results: previous.results.map((result) => (result.resultId === resultId ? { ...result, judgeFlag } : result)) }
          : previous,
      );
    },
    [runId],
  );

  /** Accepts or rejects a suggestion, and patches in the flag or review it made. Throws on failure. */
  const decideSuggestion = useCallback(
    async (suggestionId: string, accept: boolean, rejectReason?: string) => {
      const { suggestion, judgeFlag, review } = await evalsService.decideSuggestion(runId, suggestionId, accept, rejectReason);
      setDetail((previous) => {
        if (!previous) return previous;
        const flagged = suggestion.kind === 'judge-flag' && judgeFlag ? suggestion.resultId : null;
        return {
          ...previous,
          run: review ? { ...previous.run, review } : previous.run,
          results: previous.results.map((result) => (result.resultId === flagged ? { ...result, judgeFlag } : result)),
          suggestions: previous.suggestions.map((candidate) => (candidate.suggestionId === suggestionId ? suggestion : candidate)),
        };
      });
    },
    [runId],
  );

  return { detail, catalog, loading, error, reload: load, saveReview, saveJudgeFlag, decideSuggestion, cancelRun };
};
